// Static data for the Team Profile feature.
// Keep scale IDs stable — question wording can change, but IDs are persisted.

import {
  Sunrise, Coffee, MessageCircle, Video, Clock, Shield, Heart,
  Award, Flame, Leaf, Quote, Sparkle,
} from 'lucide-react';

// ── Section 1 · My Work Rhythm ───────────────────────────────────────────────
export const WORK_RHYTHM_ROWS = [
  {
    key: 'peak_hours',
    label: 'My peak hours are',
    icon: Sunrise,
    options: [
      'Early morning', 'Mid-morning', 'Midday',
      'Early afternoon', 'Late afternoon', 'Evening', 'It varies',
    ],
  },
  {
    key: 'async_channel',
    label: 'For quick async questions, reach me on',
    icon: MessageCircle,
    options: ['Slack/IM', 'Email', 'Voice note', "Whatever's open"],
  },
  {
    key: 'deeper_channel',
    label: 'For deeper conversations, I prefer',
    icon: Video,
    options: ['Video call', 'In-person', 'Async written doc', 'Phone call'],
  },
  {
    key: 'reply_window',
    label: 'My typical reply window for non-urgent messages',
    icon: Clock,
    options: [
      'Within an hour', 'Same day', 'Within 24 hours',
      'When I surface from focus',
    ],
  },
  {
    key: 'focus_blocks',
    label: 'My focus blocks are',
    icon: Shield,
    options: ['Strictly protected', 'Flexible', 'It depends on the week'],
  },
  {
    key: 'beverage',
    label: 'My beverage of choice for long meetings',
    icon: Coffee,
    options: ['Coffee', 'Tea', 'Water', 'Sparkling', 'Something stronger'],
  },
];

// ── Section 2 · What I Bring ─────────────────────────────────────────────────
export const STRENGTHS_OPTIONS = [
  'Business development', 'Customer service', 'Digital', 'Growth',
  'Leadership', 'Logistics', 'Marketing', 'Networking', 'People management',
  'Product development', 'Project management', 'Research', 'Sales', 'Strategy',
];

export const ENERGIZER_OPTIONS = [
  'Purpose', 'Mastery', 'Autonomy', 'Recognition', 'Impact',
  'Mentoring others', 'Solving hard problems', 'Shipping fast',
  'Building things that last', 'Team rhythm', 'Constructive feedback',
  'Learning',
];

export const WHAT_I_BRING_ROWS = [
  {
    key: 'strengths',
    label: 'My strengths at work',
    icon: Award,
    options: STRENGTHS_OPTIONS,
    multi: true,
  },
  {
    key: 'growing_in',
    label: "I'm currently growing in",
    icon: Flame,
    options: STRENGTHS_OPTIONS,
    multi: true,
  },
  {
    key: 'energizers',
    label: 'What energizes me beyond compensation',
    icon: Heart,
    options: ENERGIZER_OPTIONS,
    multi: true,
  },
];

// ── Section 3 · Outside of Work ──────────────────────────────────────────────
export const SPARE_TIME_OPTIONS = [
  'Cook', 'Eat', 'Exercise', 'Gardening', 'Learn new stuff',
  'Listen to podcasts', 'Make things', 'Meditate', 'Netflix',
  'Read a book', 'Spend time with friends & family', 'Travel', 'Volunteer',
];

export const OUTSIDE_ROWS = [
  {
    key: 'spare_time',
    label: 'In my spare time I like to',
    icon: Leaf,
    options: SPARE_TIME_OPTIONS,
    multi: true,
  },
  {
    key: 'misunderstood',
    label: 'One thing people often misunderstand about how I work',
    icon: Quote,
    text: true,
    maxLength: 200,
  },
  {
    key: 'cheat_code',
    label: 'My "working with me" cheat code',
    icon: Sparkle,
    text: true,
    maxLength: 200,
    placeholder: 'One quick tip for new collaborators',
  },
];

// ── Section 4 · Working With Me (12 bipolar 7-point scales) ──────────────────
export const SCALE_GROUPS = [
  {
    title: 'Communication & thinking',
    scales: [
      {
        id: 'comm_thinking_aloud',
        left: 'I think best out loud, in conversation',
        right: 'I think best on my own, then share',
      },
      {
        id: 'comm_realtime_async',
        left: 'Real-time exchange energizes me',
        right: 'Async time gives me my best work',
      },
      {
        id: 'comm_meeting_warmup',
        left: "In meetings I'm ready to dive in immediately",
        right: 'I sharpen up as the conversation develops',
      },
    ],
  },
  {
    title: 'Decisions & direction',
    scales: [
      {
        id: 'decisions_speed',
        left: 'I decide quickly and adjust as I learn',
        right: 'I gather input before committing',
      },
      {
        id: 'altitude',
        left: 'My natural altitude is strategy and the big picture',
        right: 'My natural altitude is execution and the details',
      },
      {
        id: 'problem_scope',
        left: "I'm at home with open-ended problems",
        right: "I'm at home with clearly-scoped problems",
      },
    ],
  },
  {
    title: 'Feedback & recognition',
    scales: [
      {
        id: 'feedback_directness',
        left: 'I want feedback direct and unvarnished',
        right: 'I want feedback framed with context and care',
      },
      {
        id: 'recognition_visibility',
        left: 'Recognition lands best for me publicly',
        right: 'Recognition lands best for me privately',
      },
      {
        id: 'pushback_style',
        left: 'I engage with pushback in the moment',
        right: 'I take pushback away to sit with first',
      },
    ],
  },
  {
    title: 'Pace & standards',
    scales: [
      {
        id: 'pace_polish',
        left: 'I refine until the work is polished',
        right: 'I ship on time and iterate from there',
      },
      {
        id: 'structure_freedom',
        left: 'I work best with a planned structure',
        right: 'I work best with room to follow the work',
      },
      {
        id: 'momentum_sprints',
        left: 'I prefer steady momentum across the week',
        right: 'I prefer intense sprints with real recovery',
      },
    ],
  },
];

// Map each scale ID → the column name on the profile response
export const scaleFieldFor = (id) => `scale_${id}`;

export const ALL_SCALES = SCALE_GROUPS.flatMap(g => g.scales);

// Short, human anchors used in comparison callouts.
export const SCALE_SHORT = {
  comm_thinking_aloud: ['thinking out loud', 'thinking alone first'],
  comm_realtime_async: ['real-time exchange', 'async focus time'],
  comm_meeting_warmup: ['diving straight in', 'warming up as you go'],
  decisions_speed: ['deciding fast', 'gathering input first'],
  altitude: ['the big picture', 'the details'],
  problem_scope: ['open-ended problems', 'clearly-scoped problems'],
  feedback_directness: ['direct, unvarnished feedback', 'feedback framed with context'],
  recognition_visibility: ['public recognition', 'private recognition'],
  pushback_style: ['engaging with pushback live', 'sitting with pushback first'],
  pace_polish: ['polishing the work', 'shipping then iterating'],
  structure_freedom: ['a planned structure', 'room to follow the work'],
  momentum_sprints: ['steady momentum', 'sprints with real recovery'],
};

// value 1..7 → the leaning it describes
export const leaningFor = (scaleId, value) => {
  const pair = SCALE_SHORT[scaleId];
  if (!pair || value == null) return null;
  if (value === 4) return 'a bit of both';
  return value < 4 ? pair[0] : pair[1];
};

// ── Basic info ───────────────────────────────────────────────────────────────
export const GENDER_OPTIONS = [
  'Female', 'Male', 'Non-binary', 'Prefer to self-describe', 'Prefer not to say',
];

// A curated shortlist of IANA timezones (ordered by UTC offset loosely).
// Enough to cover most teammates without being an endless wall.
export const TIMEZONE_OPTIONS = [
  'Pacific/Auckland', 'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane',
  'Australia/Perth', 'Asia/Tokyo', 'Asia/Seoul', 'Asia/Shanghai', 'Asia/Singapore',
  'Asia/Kolkata', 'Asia/Dubai', 'Europe/Moscow', 'Africa/Johannesburg',
  'Europe/Istanbul', 'Europe/Athens', 'Europe/Berlin', 'Europe/Paris',
  'Europe/Madrid', 'Europe/London', 'Europe/Dublin', 'Atlantic/Reykjavik',
  'Atlantic/Azores', 'America/Sao_Paulo', 'America/Buenos_Aires',
  'America/New_York', 'America/Toronto', 'America/Chicago', 'America/Denver',
  'America/Los_Angeles', 'America/Vancouver', 'America/Anchorage',
  'Pacific/Honolulu', 'UTC',
];
